"use client";

import type { LeaderboardEntry } from "@/components/QuestsV2/Points/Leaderboard/LeaderboardProvider";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { User } from "lucide-react";
import Image from "next/image";
import { forwardRef } from "react";

interface LeaderboardEntryItemProps {
  entry: LeaderboardEntry;
  displayName: React.ReactNode;
  isCurrentUser?: boolean;
}

const PointsDisplay = ({ points }: { points: number }) => (
  <div className="flex items-center gap-1 px-2 sm:px-3 py-1 rounded-lg backdrop-blur-2xl bg-background/20 min-w-0 flex-shrink-0">
    <div className="flex items-center justify-center min-w-0">
      <span className="text-sm sm:text-md font-bold text-foreground pr-1 sm:pr-2 truncate">
        {points.toLocaleString()}
      </span>
      <Image
        src="/assets/referral/star.gif"
        alt="Points"
        width={16}
        height={16}
        className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
      />
    </div>
  </div>
);

export const LeaderboardEntryItem = forwardRef<
  HTMLDivElement,
  LeaderboardEntryItemProps
>(({ entry, displayName, isCurrentUser = false }, ref) => {
  const textClass = isCurrentUser
    ? "text-md font-bold text-foreground"
    : "text-sm sm:text-md font-medium text-muted-foreground";

  const avatarClass = isCurrentUser ? "w-10 h-10" : "w-8 h-8 sm:w-8 sm:h-8";

  return (
    <div
      ref={ref}
      className={cn("flex items-center gap-2 sm:gap-4 lg:gap-6 py-2 min-w-0")}
    >
      <div className="flex items-center gap-2 sm:gap-4 w-16 sm:w-20 flex-shrink-0">
        <span className={cn("w-6 sm:w-8", textClass)}>{entry.rank}</span>
        <Avatar className={cn("flex-shrink-0", avatarClass)}>
          <AvatarFallback
            className={cn("flex items-center justify-center", textClass)}
            style={{
              background: isCurrentUser
                ? "linear-gradient(135deg, #F191FA, #83E3F0)"
                : undefined,
            }}
          >
            <User
              className="w-4 h-4 sm:w-5 sm:h-5"
              style={{
                color: "white",
              }}
            />
          </AvatarFallback>
        </Avatar>
      </div>
      <div className="flex-1 min-w-0">
        <span className={cn("truncate block", textClass)}>{displayName}</span>
      </div>
      <PointsDisplay points={entry.balance} />
    </div>
  );
});
